import React, { useContext } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext, getDomPath } from '@thd-nucleus/experience-context';
import { Button } from '@thd-olt-component-react/button';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { ServicesButton } from './ServicesButton';
import { ProductPodContext } from '../../ProductPodContext';
import {
  ADD_TO_CART,
  PRODUCT_POD_IRG_ATC,
  PRODUCT_POD_ATC,
  PRODUCT_POD_CLICK,
  SCHEDULE_A_MEASURE,
  REQUEST_A_QUOTE,
  SCHEDULE_A_CONSULTATION,
  BUILD_AND_BUY,
  VIEW_DETAILS,
  CHOOSE_YOUR_OPTIONS
} from '../../constants';
import {
  isATCEnabled,
  getReturnMessage,
  getCartOptions,
  getCartReqParams,
  isGenericProduct,
  isBuildAndBuyProduct,
  isLiveGoodsOOSProduct,
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  getCustomBlindsUrl,
  getScheduleAMeasureURL
} from '../../utils';

export const ProductATC = (props) => {
  const {
    channel,
    store,
    hosts
  } = useContext(ExperienceContext);

  const {
    chooseYourOptionsClick,
    isScheduleAMeasureEligible,
    noATCFulfillment,
    pageType,
    product,
    target,
    quantity,
    publishAnalytics,
    onCartSuccess,
    onCartFailure,
    showDisabledATC,
    productPodRef,
    hideATC,
    hover,
    sharedSection,
  } = useContext(ProductPodContext);

  const {
    checkGeneric,
    disabled,
    onClick,
    outline,
    showReturnable,
    className,
    silent,
  } = props;

  if (hideATC) return null;

  const isDisabled = !isATCEnabled(product) || disabled;
  const showViewDetailsBtn = isLiveGoodsOOSProduct(product);
  const showBuildAndBuyButton = isBuildAndBuyProduct(product, hover);
  const showRequestAQuoteButton = isBrioProduct(product);
  const showScheduleAConsultationButton = isCustomKitchenCabinetProduct(product);
  const showChooseYourOptionsButton = checkGeneric && isGenericProduct(product);

  const shouldHide = !isScheduleAMeasureEligible
    && !showDisabledATC
    && isDisabled
    && !showBuildAndBuyButton
    && !showViewDetailsBtn
    && !showRequestAQuoteButton
    && !showScheduleAConsultationButton;

  if (shouldHide) return null;

  const returnMessage = getReturnMessage({ showReturnable, product });
  const cartOptions = getCartOptions({ channel, hidden: !!silent, sharedSection });
  const cartReqParams = getCartReqParams({
    store, quantity, product, channel, noATCFulfillment
  });
  const isBOPIS = cartReqParams?.fulfillmentMethod === 'BOPIS';
  const isMobile = channel === 'mobile';
  const { info, identifiers } = product || {};
  const { canonicalUrl, productType = '' } = identifiers || {};
  const customExperience = info?.globalCustomConfigurator?.customExperience;
  const customBlindsUrl = getCustomBlindsUrl({
    customExperience,
    productType,
    blindsHost: hosts?.customBlinds,
    canonicalUrl,
    hover
  });
  const servicesURL = info?.productSubType?.link;

  const customBlindsButton = classNames({
    'bttn-outline bttn-outline--dark': !isMobile,
    'product-pod__customs--margin bttn--small bttn-outline bttn-outline--dark': isMobile,
  });

  const cartClicked = (event) => {
    const domPath = getDomPath(productPodRef);
    let eventName = PRODUCT_POD_ATC;

    const eventOriginatedFrom = (componentName) => {
      const component = new RegExp(componentName, 'i');
      return domPath?.reverse()?.find((name) => name?.match(component));
    };

    if (eventOriginatedFrom('itemrelatedgroups\\[')) {
      eventName = PRODUCT_POD_IRG_ATC;
    }

    const action = ADD_TO_CART;
    if (pageType !== 'dpd' && pageType !== 'pip') {
      publishAnalytics(eventName, action);

      if (eventOriginatedFrom('resultswrapped\\[')) {
        publishAnalytics(PRODUCT_POD_CLICK, action);
      }
    }

    if (onClick) {
      onClick(event, product, action);
    }
  };

  const genericClicked = (event) => {
    if (chooseYourOptionsClick) chooseYourOptionsClick(event, product);
  };

  const onBuildAndBuyClick = () => { publishAnalytics(PRODUCT_POD_CLICK, BUILD_AND_BUY); };
  const onScheduleAMeasureClick = () => { publishAnalytics(PRODUCT_POD_CLICK, SCHEDULE_A_MEASURE.toLowerCase()); };
  const onRequestAQuoteClick = () => { publishAnalytics(PRODUCT_POD_CLICK, REQUEST_A_QUOTE.toLowerCase()); };

  if (isScheduleAMeasureEligible) {
    return (
      <Button
        href={getScheduleAMeasureURL(product, pageType)}
        outline
        small={isMobile}
        tag="a"
        target="_blank"
        onClick={onScheduleAMeasureClick}
      >
        {SCHEDULE_A_MEASURE}
      </Button>
    );
  }

  if (showBuildAndBuyButton) {
    return (
      <Button
        className={customBlindsButton}
        href={customBlindsUrl}
        target={target}
        onClick={onBuildAndBuyClick}
        tag="a"
        dark
        outline
      >
        {BUILD_AND_BUY}
      </Button>
    );
  }

  if (showViewDetailsBtn) {
    return (
      <>
        <Button
          dark
          href={canonicalUrl}
          outline
          small={isMobile}
          tag="a"
          target={target}
          data-testid="bttn__view-details"
        >
          {VIEW_DETAILS}
        </Button>
      </>
    );
  }

  if (showRequestAQuoteButton) {
    return <ServicesButton name={REQUEST_A_QUOTE} servicesURL={servicesURL} onClick={onRequestAQuoteClick} />;
  }

  if (showScheduleAConsultationButton) {
    return <ServicesButton name={SCHEDULE_A_CONSULTATION} servicesURL={servicesURL} />;
  }

  if (showChooseYourOptionsButton) {
    return (
      <Button
        dark
        href={canonicalUrl}
        onClick={genericClicked}
        outline
        small={isMobile}
        tag="a"
        target={target}
      >
        {CHOOSE_YOUR_OPTIONS}
      </Button>
    );
  }

  return ((
    <>
      <AddToCart
        className={className}
        checkGeneric={checkGeneric}
        showDisabledATC={showDisabledATC}
        showReturnable={showReturnable}
        cartOptions={cartOptions}
        cartReqParams={cartReqParams}
        disabled={isDisabled}
        onClick={cartClicked}
        outline={outline}
        showBOPISOverlay={isBOPIS}
        onSuccess={onCartSuccess}
        onFail={onCartFailure}
      >
        Add To Cart
      </AddToCart>
      {returnMessage && (
        <div className="product-pod__return-messaging">
          {returnMessage}
        </div>
      )}
    </>
  ));
};

ProductATC.propTypes = {
  /** When true, checks if sku is generic and returns "Choose Your Options" button */
  checkGeneric: bool,
  className: string,
  disabled: bool,
  /** Method triggered when `checkGeneric` is true and "Choose Your Options" button clicked */
  onClick: func,
  /** "Add To Cart" Button styling */
  outline: bool,
  /** show return messaging */
  showReturnable: bool,
  /** Add to cart without overlay */
  silent: bool,
};

ProductATC.defaultProps = {
  checkGeneric: false,
  className: null,
  disabled: false,
  onClick: null,
  outline: false,
  showReturnable: false,
  silent: false,
};

ProductATC.displayName = 'ProductATC';
